<template>
  <b-card no-body class="optin-card shadow-lg mt-5">

    <!-- En-tête de la card -->
    <div class="optin-card__header">
      <div class="optin-card__logo">
        <i class="fas fa-shield-alt text-white"></i>
      </div>
      <div>
        <h5 class="optin-card__title mb-0">Validation de votre demande de devis</h5>
        <p class="optin-card__subtitle mb-0">L'Assureur Français</p>
      </div>
    </div>

    <b-card-body class="px-4 pb-4 pt-3">

      <!-- Récapitulatif client -->
      <div class="optin-card__recap mb-4">
        <p class="optin-card__recap-label">
          <i class="fas fa-user-circle mr-2 text-primary"></i>Vos informations
        </p>
        <b-row>
          <b-col cols="6">
            <div class="optin-card__recap-item">
              <span class="optin-card__recap-key">Civilité / Nom</span>
              <span class="optin-card__recap-value">
                {{ client.civilite }} {{ client.nom }} {{ client.prenom }}
              </span>
            </div>
          </b-col>
          <b-col cols="6">
            <div class="optin-card__recap-item">
              <span class="optin-card__recap-key">Email</span>
              <span class="optin-card__recap-value">{{ client.email }}</span>
            </div>
          </b-col>
          <b-col cols="6">
            <div class="optin-card__recap-item">
              <span class="optin-card__recap-key">Téléphone</span>
              <span class="optin-card__recap-value">{{ client.telephone_1 }}</span>
            </div>
          </b-col>
          <b-col cols="6">
            <div class="optin-card__recap-item">
              <span class="optin-card__recap-key">Motif de contact</span>
              <span class="optin-card__recap-value">
                <b-badge :variant="contactBadgeVariant" pill>
                  <i :class="contactIcon" class="mr-1 text-white"></i>{{ contactMotifLabel }}
                </b-badge>
              </span>
            </div>
          </b-col>
        </b-row>
      </div>
      
      <!-- Séparateur -->
      <hr class="optin-card__divider" />

      <!-- Consentement RGPD -->
      <b-form-group class="mb-1">
        <b-form-checkbox
          v-model="optinAccepted"
          :state="optinState.optin"
          class="optin-card__checkbox"
        >
          <span class="optin-card__consent-text">
            En cliquant sur <strong>« Devis gratuit »</strong>, vous acceptez que vos données soient
            utilisées par <strong>l'Assureur Français</strong> pour vous contacter par e-mail et
            par téléphone à propos de votre demande de devis. Retrouvez plus d'informations sur le
            traitement de vos données dans nos
            
            <a href="https://assureur-francais.fr/mentions-legales/"
              target="_blank"
              rel="noopener noreferrer"
              class="optin-card__link"
            >mentions légales</a>.
          </span>
        </b-form-checkbox>
        <b-form-invalid-feedback :state="optinState.optin" class="d-block mt-1 ml-4">
          Vous devez accepter les conditions pour continuer.
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Alerte erreur globale -->
      <b-alert :show="submitted" variant="danger" class="mt-3 mb-0 py-2">
        <i class="fas fa-exclamation-circle mr-2 text-white"></i>
        Veuillez corriger les champs indiqués avant de valider.
      </b-alert>

      <!-- Bouton de validation -->
      <div class="text-center mt-4">
        <b-button
          variant="primary"
          size="lg"
          block
          class="optin-card__submit-btn"
          :disabled="loading"
          @click="handleSubmit"
        >
          <b-spinner v-if="loading" small class="mr-2" />
          <i v-else class="fas fa-file-signature mr-2"></i>
          Devis gratuit
        </b-button>
        <p class="optin-card__secure-mention mt-2 mb-0">
          <i class="fas fa-lock mr-1"></i>
          Données sécurisées et confidentielles
        </p>
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
export default {
  name: 'OptinCard',

  props: {
    motif:{type: String},
    client: {type: Object      },
  },

  data() {
    return {
      contactMotif: this.motif,   // 'appel' | 'visite'
      optinAccepted: false,
      submitted: false,
      loading: false,
    };
  },

  computed: {    
    // États Bootstrap-Vue (:state) — null = neutre, false = erreur
    optinState() {
      if (!this.submitted) return { motif: null, optin: null };
      return {
        motif: !!this.contactMotif,
        optin: this.optinAccepted,
      };
    },

    contactMotifLabel() {
      if (this.contactMotif === 'appel') return 'Suite à votre appel';
      if (this.contactMotif === 'visite') return 'Suite à votre visite en agence';
      return '—';
    },

    contactBadgeVariant() {
      return this.contactMotif ? 'primary' : 'secondary';
    },

    contactIcon() {
      if (this.contactMotif === 'appel') return 'fas fa-phone-alt';
      if (this.contactMotif === 'visite') return 'fas fa-store';
      return 'fas fa-question';
    },
  },

  methods: {
    async handleSubmit() {
        if(!this.optinAccepted){
            this.submitted = true;
            return 
        }
        this.loading = true;
        this.$api.ajax('/source/optin', this.client, res => {
            if(res.status){
                location.reload();
            }else{
                // this.submitted = false;
                this.loading = false;
            }
        })
    },

    validerOptin() {
      // Simuler un appel API
      return new Promise((resolve) => setTimeout(resolve, 800));
    },
  },
};
</script>

<style scoped>
/* ─── Card ─────────────────────────────────────────────── */
.optin-card {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  max-width: 680px;
  margin: 0 auto;
}

/* ─── Header ────────────────────────────────────────────── */
.optin-card__header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, #1a56db 0%, #1e40af 100%);
  padding: 20px 24px;
  color: #fff;
}

.optin-card__logo {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.optin-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
}

.optin-card__subtitle {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.75);
}

/* ─── Récapitulatif ─────────────────────────────────────── */
.optin-card__recap {
  background: #f0f5ff;
  border-left: 4px solid #1a56db;
  border-radius: 8px;
  padding: 14px 16px;
}

.optin-card__recap-label {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
  margin-bottom: 10px;
}

.optin-card__recap-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.optin-card__recap-key {
  font-size: 0.72rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.optin-card__recap-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  word-break: break-word;
}

/* ─── Labels ────────────────────────────────────────────── */
.optin-card__field-label {
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #374151;
}

/* ─── Motif radios ──────────────────────────────────────── */
.optin-card__motif-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.optin-card__motif-radio {
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #fff;
}

.optin-card__motif-radio.is-selected {
  border-color: #1a56db;
  background: #eff6ff;
}

/* ─── Divider ───────────────────────────────────────────── */
.optin-card__divider {
  border-color: #e5e7eb;
  margin: 20px 0;
}

/* ─── Consentement ──────────────────────────────────────── */
.optin-card__consent-text {
  font-size: 0.82rem;
  color: #4b5563;
  line-height: 1.55;
}

.optin-card__link {
  color: #1a56db;
  text-decoration: underline;
}

.optin-card__link:hover {
  color: #1e40af;
}

/* ─── Bouton submit ─────────────────────────────────────── */
.optin-card__submit-btn {
  background: linear-gradient(135deg, #1a56db 0%, #1e40af 100%);
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  padding: 14px;
  letter-spacing: 0.02em;
  transition: opacity 0.2s, transform 0.15s;
}

.optin-card__submit-btn:not(:disabled):hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

/* ─── Mention sécurité ──────────────────────────────────── */
.optin-card__secure-mention {
  font-size: 0.75rem;
  color: #9ca3af;
}
</style>